import {Component, EventEmitter, Input, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';

import {AlertType} from '../widgets/alert/alert.model';
import {AddressModel} from './address-model';
import {AlertService} from '../widgets/alert/alert.service';
import {BasicPersonalDataModel} from './basic-personal-data.model';
import {CustomValidators} from '../core/validator/custom-validators';
import {DialogService} from '../dialog/dialog.service';
import {DictModel} from '../dictionary/dict.model';
import {DictService} from '../core/ecv-http-services/services/dict.service';
import {DictType} from '../enums/dict-types.enum';
import {PersonalDataModel} from './personal-data.model';
import {StepComponentAbstract} from '../flow/step.component';
import {UserService} from '../core/ecv-http-services/services/user.service';
import {ValidatorService} from '../core/validator/validator.service';
import {DictEnum} from '../enums/dict.enum';
import {Observable} from 'rxjs/Observable';
import DateUtils from '../shared/date-utils';

@Component({
  selector: 'app-personal-data',
  templateUrl: './personal-data.component.html'
})
export class PersonalDataComponent extends StepComponentAbstract implements OnInit {

  @Input() data: any;
  public personalModel: PersonalDataModel;
  public countriesDict: DictModel[] = [];
  public provincesDict: DictModel[] = [];
  public citiesDict: DictModel[] = [];
  DictType = DictType;
  DictEnum = DictEnum;
  private newDictPopupOnCloseEventEmitter = new EventEmitter();
  contactForm: FormGroup;

  constructor(private userService: UserService,
              private dictService: DictService,
              private alertService: AlertService,
              private validatorService: ValidatorService,
              private dialogService: DialogService) {
    super();
    this.personalModel = new PersonalDataModel();
    this.personalModel.basicPersonalDataModel = new BasicPersonalDataModel();
  }

  ngOnInit() {
    this.createForm();
  }

  onSave(): Observable<any> {
    if (this.isSaveInProgress) {
      return Observable.of(true);
    }
    this.personalModel.basicPersonalDataModel.firstName = this.personalModel.basicPersonalDataModel.firstName ?
      this.personalModel.basicPersonalDataModel.firstName.trim() : null;
    this.personalModel.basicPersonalDataModel.lastName = this.personalModel.basicPersonalDataModel.lastName ?
      this.personalModel.basicPersonalDataModel.lastName.trim() : null;
    this.personalModel.basicPersonalDataModel.email = this.personalModel.basicPersonalDataModel.email ?
      this.personalModel.basicPersonalDataModel.email.trim() : null;
    this.personalModel.basicPersonalDataModel.email2 = this.personalModel.basicPersonalDataModel.email2 ?
      this.personalModel.basicPersonalDataModel.email2.trim() : null;
    this.personalModel.basicPersonalDataModel.phone = this.personalModel.basicPersonalDataModel.phone ?
      this.personalModel.basicPersonalDataModel.phone.trim() : null;
    this.personalModel.basicPersonalDataModel.phone2 = this.personalModel.basicPersonalDataModel.phone2 ?
      this.personalModel.basicPersonalDataModel.phone2.trim() : null;
    this.personalModel.basicPersonalDataModel.birthDate.dateFormat = DateUtils.parseDateFormat(this.personalModel.basicPersonalDataModel.birthDate.date);
    this.personalModel.address.street = this.personalModel.address.street ?
      this.personalModel.address.street.trim() : null;
    this.personalModel.address.houseNumber = this.personalModel.address.houseNumber ?
      this.personalModel.address.houseNumber.trim() : null;
    this.personalModel.address.flatNumber = this.personalModel.address.flatNumber ?
      this.personalModel.address.flatNumber.trim() : null;

    this.validatorService.validate(this.stepForm);
    if (!this.validatorService.isValid(this.stepForm)) {
      return Observable.of(false);
    }
    if (this.personalModel.basicPersonalDataModel.userId) {
      let changes: Observable<any>[] = this.updateChanges();
      if (changes.length === 0) {
        return Observable.of(true);
      } else {
        this.isSaveInProgress = true;
        return Observable.forkJoin(changes)
          .map(() => {
            this.isSaveInProgress = false;
          }, () => {
            this.isSaveInProgress = false;
          });
      }
    }
  }

  updateChanges(): Observable<any>[] {
    let changes: Observable<any>[] = [];
    changes.push(this.userService.updateExpertPersonalData(this.personalModel.basicPersonalDataModel));
    if (this.personalModel.address) {
      if (this.personalModel.address.id) {
        changes.push(this.userService.updateExpertAddress(this.personalModel.address));
      } else {
        changes.push(this.userService.createExpertAddress(this.personalModel.address));
      }
    }
    return changes;
  }

  loadData(userId): void {
    this.userService.getExpertPersonalData(userId).subscribe(
      responseBody => {
        this.personalModel.basicPersonalDataModel = responseBody;

        this.userService.getExpertAddress(userId).subscribe(
          response => {
            if (response) {
              this.personalModel.address = response;
            } else {
              this.personalModel.address = new AddressModel();
              this.personalModel.address.userId = userId;
            }
          });
      },
      (err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          this.alertService.alert(AlertType.Error, err.error.message, false);
        }
      }
    );
  }

  openModal(title: string, dictType: number, control: AbstractControl, maxLength: number, property: string) {
    this.dialogService.open(title, dictType, control, maxLength, this.personalModel.address, property, this.newDictPopupOnCloseEventEmitter);
    this.newDictPopupOnCloseEventEmitter.subscribe((element) => {
      switch (property) {
        case 'countryDictId' :
          this.stepForm.controls['country-id'].setValue(element.id);
          break;
        case 'provinceDictId'   :
          this.stepForm.controls['province-id'].setValue(element.id);
          break;
        case 'cityDictId'   :
          this.stepForm.controls['city-id'].setValue(element.id);
          break;
      }
    });
  }

  private createForm() {
    this.contactForm = new FormGroup({
      'email': new FormControl('', {validators: [CustomValidators.validateNotEmptyEmail]}),
      'phone': new FormControl('', CustomValidators.validatePhoneNumber)
    }, CustomValidators.noEmailOrPhoneNumberValidation('email', 'phone'));
    this.stepForm = new FormGroup({
      'firstName': new FormControl('', {validators: [CustomValidators.notEmpty, CustomValidators.validateName], updateOn: 'blur'}),
      'lastName': new FormControl('', {validators: [CustomValidators.notEmpty, CustomValidators.validateName], updateOn: 'blur'}),
      'email2': new FormControl('', CustomValidators.validateNotEmptyEmail),
      'phone2': new FormControl('', CustomValidators.validatePhoneNumber),
      'birthDate': new FormControl('', CustomValidators.validateDateFormats),
      'country': new FormControl(''),
      'country-id': new FormControl('', CustomValidators.validateCorrectDict),
      'province': new FormControl(''),
      'province-id': new FormControl('', CustomValidators.validateCorrectDict),
      'city': new FormControl('', CustomValidators.notEmpty),
      'city-id': new FormControl('', CustomValidators.validateCorrectDict),
      'street': new FormControl('', CustomValidators.validateStreetName),
      'houseNumber': new FormControl('', CustomValidators.validateHouseNumberFlatNumber),
      'flatNumber': new FormControl('', CustomValidators.validateHouseNumberFlatNumber),
      'postal-code': new FormControl('', CustomValidators.validatePostalCode),
      'contact': this.contactForm
    });
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/personal-data/personal-data.component.ts